// GSAP is only downloaded on pages with the animated banner, starting straight away
const gsapLoading = document.querySelector('.hero-banner-academy[data-animation]')
    ? Promise.all([import('gsap'), import('gsap/ScrollTrigger')])
    : null;

async function initHeroBannerAcademy() {
    const section = document.querySelector('.hero-banner-academy[data-animation]');
    if (!section) return;

    const logoWrapper = section.querySelector('[data-animation-logo]');
    const dividerWrapper = section.querySelector('[data-divider]');
    if (!logoWrapper || !dividerWrapper) return;

    const [{ gsap }, { ScrollTrigger }] = await gsapLoading;
    gsap.registerPlugin(ScrollTrigger);

    const svg = logoWrapper.querySelector('svg');
    const textPath = svg.querySelectorAll('path')[0];

    const NAV_BREAKPOINT = 1400;
    const LG_BREAKPOINT = 1024;

    function getTargetSvgHeight() {
        return window.innerWidth >= LG_BREAKPOINT ? 66 : 16;
    }

    function getHeaderHeight() {
        return window.innerWidth >= NAV_BREAKPOINT ? 64 : 60;
    }

    // logoWrapper CSS: position:absolute; top:50%; transform:translateY(-50%)
    // GSAP reads yPercent:-50 from CSS transform.
    // GSAP's y is added on top → visual center = offsetTop + y
    // So y offset = dividerCenter - logoWrapper.offsetTop
    function getYOffset() {
        const dividerCenter = dividerWrapper.offsetTop + dividerWrapper.offsetHeight / 2;
        return dividerCenter - logoWrapper.offsetTop;
    }

    // =============================================
    // PHASE 1 — Natural scroll, logo animates to divider (no pin)
    // =============================================
    gsap.timeline({
        scrollTrigger: {
            trigger: section,
            start: 'top top',
            end: () => `bottom ${getHeaderHeight() + dividerWrapper.offsetHeight}`,
            scrub: true,
            invalidateOnRefresh: true,
        },
    })
        .to(
            textPath,
            {
                scale: 0,
                opacity: 0,
                transformOrigin: '50% 50%',
                duration: 0.5,
            },
            0,
        )
        .to(
            logoWrapper,
            {
                y: () => getYOffset(),
                duration: 1,
            },
            0,
        )
        .to(
            svg,
            {
                height: () => getTargetSvgHeight(),
                duration: 1,
            },
            0,
        );

    // =============================================
    // PHASE 2 — Divider + logo become fixed (sticky effect)
    // =============================================
    let stickyActive = false;
    let stickyScrollY = 0;
    let wasSticky = false;
    const TEXT_REVEAL_DISTANCE = 300;

    ScrollTrigger.create({
        trigger: section,
        start: () => `bottom ${getHeaderHeight() + dividerWrapper.offsetHeight}`,
        endTrigger: 'body',
        end: 'bottom top',
        onEnter: () => {
            // Only reset scroll baseline on fresh entry, not after refresh
            if (!wasSticky) {
                stickyScrollY = window.scrollY;
            }
            stickyActive = true;
            wasSticky = false;
            logoWrapper.setAttribute('data-animation-logo--sticky', '');

            const headerH = getHeaderHeight();
            const dividerH = dividerWrapper.offsetHeight;

            // Fix divider at header
            dividerWrapper.style.position = 'fixed';
            dividerWrapper.style.top = `${headerH}px`;
            dividerWrapper.style.bottom = 'auto';
            dividerWrapper.style.left = '0';
            dividerWrapper.style.width = '100%';
            dividerWrapper.style.zIndex = '100';

            // Fix logo centered in divider
            gsap.set(logoWrapper, {
                position: 'fixed',
                top: headerH + dividerH / 2,
                left: 0,
                width: '100%',
                y: 0,
                yPercent: -50,
                zIndex: 101,
            });

            // Re-apply text reveal (important after refresh)
            const progress = Math.min(Math.max((window.scrollY - stickyScrollY) / TEXT_REVEAL_DISTANCE, 0), 1);
            gsap.set(textPath, {
                scale: progress,
                opacity: progress,
                transformOrigin: '50% 50%',
            });
        },
        onLeaveBack: () => {
            stickyActive = false;
            logoWrapper.removeAttribute('data-animation-logo--sticky');

            // Reset text to hidden before Phase 1 takes over
            gsap.set(textPath, { scale: 0, opacity: 0, transformOrigin: '50% 50%' });

            // Restore divider to CSS defaults
            dividerWrapper.style.cssText = '';

            // Restore logo — let Phase 1 timeline manage y
            logoWrapper.style.position = '';
            logoWrapper.style.top = '';
            logoWrapper.style.left = '';
            logoWrapper.style.width = '';
            logoWrapper.style.zIndex = '';
            gsap.set(logoWrapper, { y: getYOffset(), yPercent: -50 });
        },
    });

    // =============================================
    // PHASE 3 — Text reappears on scroll while divider is sticky
    // =============================================
    function handleTextReveal() {
        if (!stickyActive) return;

        const progress = Math.min(Math.max((window.scrollY - stickyScrollY) / TEXT_REVEAL_DISTANCE, 0), 1);
        gsap.set(textPath, {
            scale: progress,
            opacity: progress,
            transformOrigin: '50% 50%',
        });
    }

    window.addEventListener('scroll', handleTextReveal, { passive: true });

    // Before refresh: restore natural positioning so GSAP reads correct layout values
    ScrollTrigger.addEventListener('refreshInit', () => {
        wasSticky = stickyActive;
        if (!stickyActive) return;

        stickyActive = false;
        dividerWrapper.style.cssText = '';
        logoWrapper.style.position = '';
        logoWrapper.style.top = '';
        logoWrapper.style.left = '';
        logoWrapper.style.width = '';
        logoWrapper.style.zIndex = '';
    });

    window.addEventListener('resize', () => ScrollTrigger.refresh());
}

// Init after full page load so layout (images) is settled before GSAP reads values
if (document.readyState === 'complete') {
    initHeroBannerAcademy();
} else {
    window.addEventListener('load', initHeroBannerAcademy);
}
